/**
 * Mirror Moment™ — the defining beat of Flow A (Experience Engine™ v2.0).
 * Black screen, quiet animation, canonical copy, pause, one action.
 *
 * Canonical copy is versioned content (P-EXP-03) — never generated at runtime.
 * Inserted between assessment completion and the report; changes nothing
 * about the report or the assessment pipeline.
 *
 * Design decisions (WeKIT Experience Review™, 2026-07-27):
 *  - Pure black (`bg-black`), NOT `--background` (near-black 4%): the canon
 *    reads "black screen" (02_VISION) — this is doctrinal, not drift.
 *  - Prior lines dim to 60% as the next arrives, so each beat has a single
 *    point of light; a longer breath precedes the third line.
 *  - Continue receives focus when it appears (announced to screen readers).
 *  - Reduced-motion users see the full moment immediately — the pause is
 *    delivered through motion; without motion it would only be a delay.
 *  - A quiet Skip is available for repeat visits; it honours the same
 *    destination as Continue.
 *  - The report is prefetched during the pause so Continue lands instantly.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHead from "@/components/PageHead";
import { useAuth } from "@/contexts/AuthContext";
import { prefetchStudentReport } from "@/lib/reportPrefetch";
import { MIRROR_MOMENT } from "@/config/experience";

const MIRROR_COPY = [
  "We don't believe a score can define you.",
  "But we do believe your strengths tell a story.",
  "Let's discover yours.",
];

/** Pacing is presentation config — see src/config/experience.ts. */
const STEP_DELAYS_MS = MIRROR_MOMENT.stepDelaysMs;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function MirrorMoment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(() => (prefersReducedMotion() ? STEP_DELAYS_MS.length : 0));
  const continueRef = useRef<HTMLButtonElement>(null);

  // Use the pause: fetch (or assemble) the report now so Continue is instant.
  useEffect(() => {
    if (user?.id) prefetchStudentReport(user.id).catch(() => { /* report page handles errors */ });
  }, [user?.id]);

  useEffect(() => {
    if (step >= STEP_DELAYS_MS.length) return;
    const t = setTimeout(() => setStep((s) => s + 1), STEP_DELAYS_MS[step]);
    return () => clearTimeout(t);
  }, [step]);

  const done = step >= STEP_DELAYS_MS.length;
  const visibleLines = Math.min(step + 1, MIRROR_COPY.length);
  const currentLine = Math.min(step, MIRROR_COPY.length - 1);

  useEffect(() => {
    if (done) continueRef.current?.focus();
  }, [done]);

  const toReport = () => navigate("/report", { replace: true });

  return (
    <main className="min-h-dvh bg-black flex flex-col items-center justify-center text-center px-6">
      <PageHead
        title="Your story begins | WeKIT™ Career Clarity 360"
        description="A quiet moment between your assessment and your story."
        path="/mirror"
        noIndex
      />
      <div aria-live="polite" className="max-w-md">
        {MIRROR_COPY.slice(0, visibleLines).map((copy, i) => (
          <p
            key={copy}
            className={`font-display text-xl sm:text-2xl font-medium my-4 animate-fade-in transition-opacity duration-700 ${
              i === 2 ? "text-primary" : "text-foreground"
            } ${i < currentLine ? "opacity-60" : "opacity-100"}`}
          >
            {copy}
          </p>
        ))}
      </div>
      <div className="h-16 mt-8">
        {done && (
          <button
            ref={continueRef}
            onClick={toReport}
            className="animate-fade-in inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {!done && (
        <button
          onClick={toReport}
          aria-label="Skip to your report"
          className="fixed bottom-6 right-6 text-xs text-muted-foreground/60 hover:text-muted-foreground focus-visible:text-muted-foreground transition-colors"
        >
          Skip
        </button>
      )}
    </main>
  );
}
